
import { useState, useRef, useEffect } from 'react';

interface Message {
  id: number;
  text: string;
  isUser: boolean;
  timestamp: Date;
}

export default function EcoPilot() {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState<Message[]>([
    {
      id: 1,
      text: 'Merhaba! Ben EcoPilot, sürdürülebilirlik asistanınızım. Size nasıl yardımcı olabilirim?',
      isUser: false,
      timestamp: new Date()
    }
  ]);
  const [inputValue, setInputValue] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const getResponse = (userMessage: string): string => {
    const lowerMessage = userMessage.toLowerCase();
    
    if (lowerMessage.includes('merhaba') || lowerMessage.includes('selam')) {
      return 'Merhaba! Size sürdürülebilirlik konularında yardımcı olmaktan mutluluk duyarım. Hangi konuda bilgi almak istiyorsunuz?';
    }
    
    if (lowerMessage.includes('karbon') || lowerMessage.includes('carbon')) {
      return 'Karbon ayak izi, işletmenizin sera gazı emisyonlarının hesaplanmasıdır. ISO 14064-1 standardında karbon ayak izi danışmanlığı sunuyoruz. Bu konuda detaylı bilgi için teklif alabilirsiniz.';
    }
    
    if (lowerMessage.includes('gri') || lowerMessage.includes('rapor')) {
      return 'GRI (Global Reporting Initiative) sürdürülebilirlik raporlaması için küresel standartlardır. Modern yaklaşımlarla GRI uyumlu sürdürülebilirlik raporları hazırlıyoruz.';
    }
    
    if (lowerMessage.includes('esg')) {
      return 'ESG (Çevresel, Sosyal, Yönetişim) danışmanlığında uzmanız. İşletmenizin ESG performansını değerlendirip iyileştirme stratejileri geliştiriyoruz.';
    }
    
    if (lowerMessage.includes('su') || lowerMessage.includes('water')) {
      return 'Su ayak izi danışmanlığı hizmetimizle ISO 14046 standardında su tüketim değerlendirmesi yapıyoruz. Sürdürülebilir su yönetimi için destek sağlıyoruz.';
    }
    
    if (lowerMessage.includes('cbam') || lowerMessage.includes('skdm')) {
      return 'CBAM/SKDM (Sınırda Karbon Düzenleme Mekanizması) AB\'ye ihracat yapan şirketler için zorunlu hale gelecek. Bu konuda uzman danışmanlık sağlıyoruz.';
    }
    
    if (lowerMessage.includes('fiyat') || lowerMessage.includes('ücret') || lowerMessage.includes('maliyet')) {
      return 'Hizmet ücretlerimiz projenizin kapsamına göre değişiklik gösterir. Ücretsiz ön değerlendirme için teklif formumuzu doldurabilirsiniz.';
    }
    
    if (lowerMessage.includes('randevu') || lowerMessage.includes('görüşme')) {
      return 'Randevu almak için sayfanın üst kısmındaki "Randevu Al" butonunu kullanabilir veya WhatsApp üzerinden iletişime geçebilirsiniz.';
    }
    
    if (lowerMessage.includes('iletişim') || lowerMessage.includes('telefon')) {
      return 'Bize +90 543 437 30 49 numaralı telefondan ulaşabilir veya info@ecoventra.com adresine e-posta gönderebilirsiniz.';
    }
    
    return 'Bu konuda size yardımcı olmak için daha fazla bilgiye ihtiyacım var. Lütfen daha spesifik bir soru sorar mısınız? Alternatif olarak uzman ekibimizle iletişime geçebilirsiniz.';
  };

  const handleSendMessage = async () => {
    if (!inputValue.trim()) return;

    const userMessage: Message = {
      id: messages.length + 1,
      text: inputValue,
      isUser: true,
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    setInputValue('');
    setIsTyping(true);

    // Simulate typing delay
    setTimeout(() => {
      const botResponse: Message = {
        id: messages.length + 2,
        text: getResponse(inputValue),
        isUser: false,
        timestamp: new Date()
      };
      
      setMessages(prev => [...prev, botResponse]);
      setIsTyping(false);
    }, 1000 + Math.random() * 1000);
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  return (
    <div className="fixed bottom-24 right-6 z-40">
      {/* Chat Toggle Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-14 h-14 bg-emerald-600 hover:bg-emerald-700 text-white rounded-full shadow-lg hover:shadow-xl transition-all duration-300 flex items-center justify-center group"
      >
        {isOpen ? (
          <i className="ri-close-line text-xl"></i>
        ) : (
          <div className="relative">
            <i className="ri-robot-line text-xl"></i>
            <div className="absolute -top-1 -right-1 w-3 h-3 bg-green-400 rounded-full animate-pulse"></div>
          </div>
        )}
      </button>

      {/* Chat Window */}
      {isOpen && (
        <div className="absolute bottom-16 right-0 w-80 h-96 bg-white rounded-2xl shadow-2xl border border-gray-200 flex flex-col animate-in slide-in-from-bottom-4 fade-in duration-300">
          {/* Header */}
          <div className="bg-emerald-600 text-white p-4 rounded-t-2xl">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-emerald-500 rounded-full flex items-center justify-center">
                <i className="ri-robot-line text-lg"></i>
              </div>
              <div>
                <h3 className="font-semibold">EcoPilot</h3>
                <p className="text-xs text-emerald-100">Sürdürülebilirlik Asistanı</p>
              </div>
              <div className="ml-auto">
                <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></div>
              </div>
            </div>
          </div>

          {/* Messages */}
          <div className="flex-1 overflow-y-auto p-4 space-y-3">
            {messages.map((message) => (
              <div
                key={message.id}
                className={`flex ${message.isUser ? 'justify-end' : 'justify-start'}`}
              >
                <div
                  className={`max-w-[80%] p-3 rounded-2xl text-sm ${
                    message.isUser
                      ? 'bg-emerald-600 text-white rounded-br-md'
                      : 'bg-gray-100 text-gray-800 rounded-bl-md'
                  }`}
                >
                  {message.text}
                </div>
              </div>
            ))}
            
            {isTyping && (
              <div className="flex justify-start">
                <div className="bg-gray-100 text-gray-800 p-3 rounded-2xl rounded-bl-md text-sm">
                  <div className="flex space-x-1">
                    <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"></div>
                    <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.1s' }}></div>
                    <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
                  </div>
                </div>
              </div>
            )}
            
            <div ref={messagesEndRef} />
          </div>

          {/* Input */}
          <div className="p-4 border-t border-gray-200">
            <div className="flex space-x-2">
              <input
                type="text"
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder="Mesajınızı yazın..."
                className="flex-1 px-3 py-2 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 text-sm"
              />
              <button
                onClick={handleSendMessage}
                disabled={!inputValue.trim()}
                className="w-9 h-9 bg-emerald-600 text-white rounded-xl hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center"
              >
                <i className="ri-send-plane-line text-sm"></i>
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
